import { combineSlices, createSelector } from '@reduxjs/toolkit'

import { hasTenMinutesPassed } from '../utils'

import { documentSummarySlice, selectDocumentSummaryExecutionLoading, selectDocumentSummaryLoading, selectDocumentSummaryResult } from './documentSummary'
import { documentBatchSlice, selectDocumentBatchDocuments, selectDocumentBatchLoading } from './documentBatch'
import { documentSlice, selectDocument, selectDocumentLoading } from './document'
import { documentProspectingSlice } from './documentProspecting'
import { documentSidebarSlice } from './documentSidebar'
import { documentFilterSlice } from './documentFilter'
import { documentTypeSlice, selectDocumentTypes } from './documentTypes'

export default combineSlices(
  documentSummarySlice,
  documentTypeSlice,
  documentProspectingSlice,
  documentBatchSlice,
  documentSlice,
  documentSidebarSlice,
  documentFilterSlice,
)

export const selectShouldPollDocument = createSelector(
  [selectDocumentLoading, selectDocumentSummaryLoading, selectDocument, selectDocumentSummaryResult],
  (documentLoading, documentSummaryLoading, document, documentSummary) => {
    if (documentLoading || documentSummaryLoading || !document || documentSummary) {
      return false
    }

    const { typeProcessing, summaryProcessing } = document

    if (summaryProcessing.finishedAt) {
      return false
    }

    if (summaryProcessing.startedAt) {
      return !hasTenMinutesPassed(summaryProcessing.startedAt)
    }

    if (typeProcessing.finishedAt) {
      return !hasTenMinutesPassed(typeProcessing.finishedAt)
    }

    if (typeProcessing.startedAt) {
      return !hasTenMinutesPassed(typeProcessing.startedAt)
    }

    return true
  },
)

export const selectShouldPollDocumentBatch = createSelector([selectDocumentBatchLoading, selectDocumentBatchDocuments], (documentBatchLoading, documents) => {
  if (documentBatchLoading || !documents.length) {
    return false
  }

  return documents.some((document) => {
    const { typeProcessing, summaryProcessing } = document

    if (summaryProcessing.finishedAt) {
      return false
    }

    if (summaryProcessing.startedAt) {
      return !hasTenMinutesPassed(summaryProcessing.startedAt)
    }

    if (typeProcessing.finishedAt) {
      return !hasTenMinutesPassed(typeProcessing.finishedAt)
    }

    if (typeProcessing.startedAt) {
      return !hasTenMinutesPassed(typeProcessing.startedAt)
    }

    return true
  })
})

export const selectShouldPollDocumentSummary = createSelector(
  [selectShouldPollDocument, selectDocument, selectDocumentSummaryResult, selectDocumentSummaryExecutionLoading],
  (shouldPollDocument, document, documentSummaryResult, documentSummaryExecutionLoading) => {
    if (shouldPollDocument || documentSummaryResult || !document || documentSummaryExecutionLoading) {
      return false
    }

    const { summaryProcessing } = document

    return !!summaryProcessing.finishedAt && !hasTenMinutesPassed(summaryProcessing.finishedAt)
  },
)

export const selectDocumentType = createSelector([selectDocument, selectDocumentTypes], (document, documentTypes) => {
  if (!document || !documentTypes.length) {
    return null
  }

  const { typeId } = document.typeProcessing
  if (!typeId) {
    return null
  }

  const documentType = documentTypes.find((documentType) => documentType.id === typeId)
  if (!documentType) {
    return null
  }

  return documentType
})
